/**
 * Logged-in marketing/analytics preferences PUT (platform-wide).
 */
import { readAnonymousKey } from '../../shared/analytics/analyticsAnonymousStorage.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import { loadRuntimeConfig } from '../../config/runtimeConfig.js';
import { buildConsentPreferencesPayload } from './consentPreferencesPayload.js';
import { resolveConsentStorageTenantCode } from './consentStorageTenant.js';
import { putPlatformConsentPreferences, type ConsentApiRolloutFallback } from './platformConsentApi.js';

export interface SaveMarketingAnalyticsPreferencesInput {
  readonly analytics: boolean;
  readonly marketing: boolean;
}

export async function saveMarketingAnalyticsConsentPreferences(
  input: SaveMarketingAnalyticsPreferencesInput,
  options?: {
    readonly anonymousKey?: string | null;
    readonly rollout?: ConsentApiRolloutFallback;
  },
): Promise<void> {
  const storageTenant = resolveConsentStorageTenantCode();
  const body = buildConsentPreferencesPayload(
    storageTenant,
    {
      analytics: input.analytics,
      marketing: input.marketing,
    },
    { anonymousKey: options?.anonymousKey },
  );
  if (!input.analytics && !('anonymousKey' in body)) {
    customerLogger.warn(
      'marketing analytics consent revoke without anonymousKey — server erasure by customerId only',
    );
  }

  const { apiBaseUrl } = loadRuntimeConfig();
  await putPlatformConsentPreferences({
    apiBaseUrl,
    body,
    ...(options?.rollout !== undefined ? { rollout: options.rollout } : {}),
  });
}

export function readAnonymousKeyForMarketingSave(
  tenantCode: string,
  analytics: boolean,
): string | null {
  const storageTenant = resolveConsentStorageTenantCode(tenantCode);
  return analytics ? null : readAnonymousKey(storageTenant);
}
